<template>
    <stat-card :expandable="expandable" :expanded="expanded" @toggle:expand="$emit('toggle:expand')">
        <template #header>
            <kimsa-combobox class="vc-metric" dense solo :items="typeOptions" @change="type = $event" :value="type"
                placeholder-class no-object multipleWithChips limitCharts height="30" />
        </template>

        <div class="vc-toolbar">
            <div class="vc-legend">
                <span v-for="l in legend" :key="l.label" class="vc-leg">
                    <span v-if="l.dashed" class="vc-leg-line" :style="{ borderTopColor: l.color }" />
                    <span v-else class="vc-leg-sq" :style="{ background: l.color }" />{{ l.label }}
                </span>
                <span v-if="hasWellnessDots" class="vc-leg">
                    <span class="vc-leg-dot" />{{ $t('statistics.wellness') }}
                </span>
            </div>
            <group-by-selector @change="currentGroupBy = $event" :value="currentGroupBy" />
        </div>

        <line-chart :labels="labels" :datasets="datasets" :styles="chartStyles" ref="chart" :offset-x="needOffsetX" />

        <div v-if="hasWellnessDots" class="vc-wdots" :style="dotsStyle">
            <div class="vc-wdots-track">
                <div v-for="(dot, i) in wellnessDots" :key="i" class="vc-wdot-slot" :style="dotStyle(i)">
                    <v-tooltip v-if="dot.value !== null" top :color="tooltipBg" content-class="vc-wtt">
                        <template #activator="{ on, attrs }">
                            <span class="vc-wdot" :style="{ background: wellnessColor(dot.value) }" v-on="on"
                                v-bind="attrs" />
                        </template>
                        <div class="vc-wtt-inner">
                            <div class="vc-wtt-date">{{ dot.label }}</div>
                            <div class="vc-wtt-row">
                                {{ $t('statistics.wellness') }} <b>{{ dot.value }}/25</b>
                            </div>
                        </div>
                    </v-tooltip>
                    <span v-else class="vc-wdot vc-wdot--empty" />
                </div>
            </div>
        </div>
    </stat-card>
</template>

<script>
import GroupBySelector from "@/components/statistics/GroupBySelector";
import LineChart from "@/components/common/LineChart";
import _groupBy from "lodash/groupBy";
import KimsaCombobox from '../common/inputs/KimsaCombobox.vue';
import StatCard from './StatCard.vue';
import { expandableCardMixin } from '@/mixins/expandableCard';
import { sumWellnessPreInfo } from "../../utils/Routine";

const WELLNESS_COLORS = { high: '#339999', mid: '#E6AC00', low: '#E85C5C' }

export default {
    name: "VariablesChart",
    components: { LineChart, GroupBySelector, KimsaCombobox, StatCard },
    mixins: [expandableCardMixin],
    props: {
        docs: {
            type: Array,
            default: null,
        },
        compareDocs: {
            type: Array,
            default: null,
        },
        compareLabel: {
            type: String,
            default: null,
        }
    },
    data(vm) {
        return {
            currentGroupBy: 'daily',
            typeOptions: [
                { name: vm.$t('statistics.time_rpe_charge'), value: 'time_rpe_charge' },
                { name: vm.$t('statistics.sets_total'), value: 'sets_total' },
                { name: vm.$t('statistics.sets_strength'), value: 'sets_strength' },
                { name: vm.$t('statistics.sets_speed'), value: 'sets_speed' },
                { name: vm.$t('statistics.sets_resistance'), value: 'sets_resistance' },
                { name: vm.$t('statistics.intensity_strength'), value: 'intensity_strength' },
                { name: vm.$t('statistics.tonnage'), value: 'tonnage' },
                { name: vm.$t('statistics.meters'), value: 'distance' },
                { name: vm.$t('statistics.minutes'), value: 'minutes' },
            ],
            type: ['time_rpe_charge'],
            routines: [],
            datasets: [],
            labels: [],
            legend: [],
            wellnessDots: [],
            dotsPadding: { left: 0, right: 0 },
            chartStyles: { position: 'relative', height: '220px' },
            rpeTable: [
                { name: 10, reps: [100, 95, 91, 87, 85, 83, 81, 79] },
                { name: 9.5, reps: [97, 93, 89, 86, 84, 82, 80, 77.5] },
                { name: 9, reps: [95, 91, 87, 85, 83, 81, 79, 76] },
                { name: 8.5, reps: [93, 89, 86, 84, 82, 80, 77.5, 74.5] },
                { name: 8, reps: [91, 87, 85, 83, 81, 79, 76, 73] },
                { name: 7.5, reps: [89, 86, 84, 82, 80, 77.5, 74.5, 71.5] },
                { name: 7, reps: [87, 85, 83, 81, 79, 76, 73, 70] },
            ],
            rirTable: [
                { name: 0, reps: [100, 95, 91, 87, 85, 83, 81, 79] },
                { name: 1, reps: [95, 91, 87, 85, 83, 81, 79, 76] },
                { name: 2, reps: [91, 87, 85, 83, 81, 79, 76, 73] },
                { name: 3, reps: [89, 86, 84, 82, 80, 77.5, 74.5, 71.5] },
                { name: 4, reps: [87, 85, 83, 81, 79, 76, 73, 70] },
            ],
        }
    },
    computed: {
        hasCompare(vm = this) {
            return !!(vm.compareDocs && vm.compareDocs.length)
        },
        compareTitle(vm = this) {
            return vm.compareLabel || vm.$t('common.team')
        },
        needOffsetX(vm = this) {
            return vm.datasets.length > 2
        },
        hasWellnessDots(vm = this) {
            return vm.labels.length > 0 && vm.wellnessDots.some(dot => dot.value !== null)
        },
        dotsStyle(vm = this) {
            return { paddingLeft: `${vm.dotsPadding.left}px`, paddingRight: `${vm.dotsPadding.right}px` }
        },
        tooltipBg(vm = this) {
            return vm.$vuetify.theme.dark ? '#0C0E12' : '#FFFFFF'
        },
    },
    mounted() {
        const vm = this
        vm.$nextTick(() => vm.setCharts())
        window.addEventListener('resize', vm.updateDotsPadding)
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.updateDotsPadding)
        if (this._dotsTimer) clearTimeout(this._dotsTimer)
    },
    methods: {
        setCharts() {
            const vm = this;
            const currentDocs = vm.docs || []
            const grouped = vm.groupRoutines(vm.loadStats(currentDocs))
            const compareGrouped = vm.hasCompare ? vm.groupRoutines(vm.loadStats(vm.compareDocs)) : []
            const { datasets, labels, legend } = vm.loadDatasets(grouped, compareGrouped)
            vm.datasets = datasets
            vm.labels = labels
            vm.legend = legend || []
            vm.wellnessDots = vm.buildWellnessDots(currentDocs, labels)
            vm.$nextTick(() => requestAnimationFrame(vm.updateDotsPadding))
            if (vm._dotsTimer) clearTimeout(vm._dotsTimer)
            vm._dotsTimer = setTimeout(vm.updateDotsPadding, 250)
        },
        bucketLabel(date) {
            const vm = this
            let current = vm.$moment(date)
            if (vm.currentGroupBy === 'weekly') current = current.startOf('week')
            if (vm.currentGroupBy === 'monthly') current = current.startOf('month')
            return current.format('DD MMM YY').replace('.', '')
        },
        buildWellnessDots(docs, labels) {
            const vm = this
            const grouped = {}
            docs.forEach(doc => {
                if (!doc?.advance?.pre || !doc?.advance?.preInfo || !doc?.rawDate?.full) return
                const value = sumWellnessPreInfo(doc.advance.preInfo)
                if (!value) return
                const key = vm.bucketLabel(doc.rawDate.full)
                if (!grouped[key]) grouped[key] = []
                grouped[key].push(value)
            })

            return labels.map(label => {
                const values = grouped[label]
                if (!values?.length) return { label, value: null }
                const average = values.reduce((total, value) => total + value, 0) / values.length
                return { label, value: Number(average.toFixed(1)) }
            })
        },
        wellnessColor(value) {
            return value >= 18 ? WELLNESS_COLORS.high : value >= 12 ? WELLNESS_COLORS.mid : WELLNESS_COLORS.low
        },
        dotStyle(index) {
            const vm = this
            const total = vm.wellnessDots.length
            if (total === 0) return null
            const percent = vm.needOffsetX
                ? ((index + 0.5) / total) * 100
                : (total > 1 ? (index / (total - 1)) * 100 : 50)
            return { left: `${percent}%` }
        },
        updateDotsPadding() {
            const vm = this
            const chart = vm.$refs.chart?.$data?._chart
            if (!chart?.chartArea) return
            const width = chart.width || chart.canvas?.clientWidth || 0
            const left = Math.round(chart.chartArea.left)
            const right = Math.max(0, Math.round(width - chart.chartArea.right))
            if (vm.dotsPadding.left !== left || vm.dotsPadding.right !== right)
                vm.dotsPadding = { left, right }
        },
        groupRoutines(routines) {
            const vm = this;
            let currentRoutines = routines.map(routine => {
                let date = vm.$moment(routine.date)
                if (vm.currentGroupBy === 'weekly') date = date.startOf('week')
                if (vm.currentGroupBy === 'monthly') date = date.startOf('month')

                return {
                    ...routine,
                    date: date,
                    dateString: date.format('DD MMM YY').replace('.', ''),
                }
            })

            if (vm.currentGroupBy !== 'daily') {
                let grouped = _groupBy(currentRoutines, d => d.dateString)

                currentRoutines = []
                for (let currentDate in grouped) {
                    let group = grouped[currentDate]
                    if (group.length === 1) {
                        currentRoutines.push(group[0]);
                    } else {
                        let accumulatedValues = {};
                        group.forEach(item => {
                            for (let key in item) {
                                if (key !== 'date' && key !== 'dateString') {
                                    accumulatedValues[key] = (accumulatedValues[key] || 0) + item[key];
                                    if (vm.type.includes('intensity_strength')) accumulatedValues.intensity_strength_count = group.filter(cg => cg.intensity_strength > 0).length
                                }
                            }
                        });

                        currentRoutines.push({
                            date: group[0].date,
                            dateString: group[0].dateString,
                            ...accumulatedValues
                        });
                    }
                }
            }

            return currentRoutines
        },
        loadStats(docs) {
            const vm = this;
            return docs.map(doc => {
                const duration = Number(doc?.advance?.postInfo?.duration || 0)
                const effort = Number(doc?.advance?.postInfo?.effort || 0)
                const charge = duration * effort
                const minutes = doc.type === 'competence' ? duration : 0

                let sets_total = 0
                let sets_strength = 0
                let sets_speed = 0
                let sets_resistance = 0
                let percentage = 0
                let rpe = 0
                let rir = 0
                let tonnage = 0
                let distance = 0
                let quantity_strong = 0
                doc.blocks?.forEach(block => {
                    block.exercises.forEach(exercise => {
                        if (exercise.type !== 'rest') {
                            let sets = Number(exercise.config?.sets?.length || 0)
                            sets_total += sets
                            if (vm.categoryKey(exercise.categories?.[0]) === vm.CATEGORY.STRONG) sets_strength += sets
                            if (vm.categoryKey(exercise.categories?.[0]) === vm.CATEGORY.SPEED) sets_speed += sets
                            if (vm.categoryKey(exercise.categories?.[0]) === vm.CATEGORY.RESISTANCE) sets_resistance += sets

                            exercise.config?.sets?.forEach((set, i) => {
                                if (vm.categoryKey(exercise.categories?.[0]) === vm.CATEGORY.STRONG) {
                                    let reps = vm.takeValueFromSet(set, 'reps') || 0
                                    tonnage += (vm.takeValueFromSet(set, 'weight') * reps)
                                    let valuePercentage = vm.takeValueFromSet(set, 'percentage')
                                    percentage += valuePercentage
                                    let valueRpe = vm.takeValueFromSet(set, 'rpe')
                                    rpe += valueRpe ? vm.getValueFromTable(valueRpe, set, 'rpeTable') : 0
                                    let valueRir = vm.takeValueFromSet(set, 'rir')
                                    rir += valueRir ? vm.getValueFromTable(valueRir, set, 'rirTable') : 0
                                    if (valuePercentage > 0 || valueRir > 0 || valueRpe > 0) quantity_strong++
                                } else if (vm.categoryKey(exercise.categories?.[0]) === vm.CATEGORY.SPEED || vm.categoryKey(exercise.categories?.[0]) === vm.CATEGORY.RESISTANCE) {
                                    distance += (vm.takeValueFromSet(set, 'distance') * sets)
                                }
                            })
                        }
                    })
                })

                let intensity_strength = percentage + rpe + rir
                if (quantity_strong > 0) intensity_strength /= quantity_strong

                return {
                    date: doc.rawDate.full,
                    duration: duration,
                    effort: effort,
                    charge: charge,
                    sets_total: sets_total,
                    sets_strength: sets_strength,
                    sets_speed: sets_speed,
                    sets_resistance: sets_resistance,
                    intensity_strength: Number(Number(intensity_strength).toFixed(1)),
                    tonnage: tonnage,
                    distance: distance,
                    minutes: minutes,
                }
            })
        },
        loadDatasets(groupRoutines, compareRoutines = []) {
            const vm = this
            const bg = vm.$vuetify.theme.currentTheme.bg
            const round1 = n => Number(Number(n).toFixed(1))
            const byDate = (a, b) => vm.$moment(a, 'DD MMM YY').toDate() - vm.$moment(b, 'DD MMM YY').toDate()

            const sorted = groupRoutines.slice().sort(
                (a, b) => vm.$moment(a.date).valueOf() - vm.$moment(b.date).valueOf()
            )

            const RGB = { '#339999': '51, 153, 153', '#CCFF33': '204, 255, 51', '#66CCCC': '102, 204, 204' }
            const COL = ['#339999', '#CCFF33', '#66CCCC']

            const canvas = vm.$refs.chart?.$refs?.canvas
            let gradientFill = 'rgba(51, 153, 153, 0.15)'
            if (canvas) {
                gradientFill = canvas.getContext('2d').createLinearGradient(0, 0, 0, 220)
                gradientFill.addColorStop(0, 'rgba(51, 153, 153, 0.35)')
                gradientFill.addColorStop(1, 'rgba(51, 153, 153, 0.02)')
            }

            const line = (color) => ({
                borderColor: color,
                pointBackgroundColor: color,
                pointBorderColor: bg,
                pointBorderWidth: 1.5,
                pointRadius: 4,
                pointHoverRadius: 5,
                borderWidth: 2,
                lineTension: 0,
            })
            const bar = (color) => ({
                backgroundColor: `rgba(${RGB[color]}, 0.5)`,
                borderColor: `rgba(${RGB[color]}, 0.65)`,
                borderWidth: 1,
            })

            const compareSorted = compareRoutines.slice().sort(
                (a, b) => vm.$moment(a.date).valueOf() - vm.$moment(b.date).valueOf()
            )
            const compareSeries = (labels, key, yAxisID) => {
                if (!compareSorted.length) return null
                const data = vm.alignSeries(labels, compareSorted, key)
                if (!data.some(value => value !== null)) return null
                return {
                    label: vm.compareTitle,
                    data,
                    type: 'line',
                    fill: false,
                    spanGaps: true,
                    yAxisID,
                    borderDash: [4, 3],
                    ...line(COL[2]),
                    pointRadius: 3,
                    pointHoverRadius: 4,
                }
            }

            if (vm.type.includes('time_rpe_charge')) {
                const labels = Array.from(new Set(
                    sorted.filter(r => r.charge > 0 || r.duration > 0 || r.effort > 0).map(r => r.dateString)
                )).sort(byDate)
                const chargeData = vm.alignSeries(labels, sorted, 'charge')
                const durData = vm.alignSeries(labels, sorted, 'duration')
                const rpeData = vm.alignSeries(labels, sorted, 'effort')
                const result = {
                    labels,
                    legend: [
                        { label: vm.$t('exercises.charge'), color: COL[1] },
                        { label: vm.$t('exercises.duration'), color: COL[2] },
                        { label: vm.$t('exercises.rpe'), color: COL[0] },
                    ],
                    datasets: [
                        { label: vm.$t('exercises.charge'), data: chargeData, type: 'bar', yAxisID: 'left-axis', ...bar(COL[1]) },
                        { label: vm.$t('exercises.duration'), data: durData, type: 'line', fill: false, spanGaps: true, yAxisID: 'left-axis', ...line(COL[2]) },
                        { label: vm.$t('exercises.rpe'), data: rpeData, type: 'line', fill: false, spanGaps: true, yAxisID: 'right-axis', ...line(COL[0]) },
                    ]
                }
                const compare = compareSeries(labels, 'effort', 'right-axis')
                if (compare) {
                    result.datasets.push(compare)
                    result.legend.push({ label: vm.compareTitle, color: COL[2], dashed: true })
                }
                return result
            }

            const labelsSet = new Set()
            vm.type.forEach(type => {
                sorted.forEach(routine => {
                    if (routine[type] > 0) labelsSet.add(routine.dateString)
                })
            })
            const labels = Array.from(labelsSet).sort(byDate)
            const data = vm.type.map(type => vm.alignSeries(labels, sorted, type))

            const datasets = []
            const legend = []
            vm.type.forEach((type, i) => {
                if (!data[i] || !data[i].some(value => value !== null)) return
                const color = COL[i % COL.length]
                legend.push({ label: vm.$t(`statistics.${type}`), color })
                if (i === 1) {
                    datasets.push({ label: vm.$t(`statistics.${type}`), data: data[i], type: 'bar', yAxisID: 'left-axis', ...bar(color) })
                } else {
                    datasets.push({
                        label: vm.$t(`statistics.${type}`), data: data[i], type: 'line', yAxisID: 'left-axis',
                        fill: i === 0, backgroundColor: i === 0 ? gradientFill : 'transparent', ...line(color),
                    })
                }
            })

            const compare = compareSeries(labels, vm.type[0], 'left-axis')
            if (compare) {
                datasets.push(compare)
                legend.push({ label: vm.compareTitle, color: COL[2], dashed: true })
            }

            return { datasets, labels, legend }
        },
        alignSeries(labels, rows, key) {
            const round1 = n => Number(Number(n).toFixed(1))
            const byLabel = {}

            rows.forEach(row => {
                const value = row[key]
                if (!(value > 0)) return
                byLabel[row.dateString] = row['intensity_strength_count']
                    ? round1(value / row['intensity_strength_count'])
                    : round1(value)
            })

            return labels.map(label => byLabel[label] != null ? byLabel[label] : null)
        },
        takeValueFromSet(set, attribute) {
            const vm = this
            let defaultValue = 0
            let value = set.data.find(data => data.short_name === `short_${attribute}`)
            return !!value || value === 0 ? Number(value.value || 0) : defaultValue
        },
        getValueFromTable(value, set, typeTable) {
            const vm = this
            if (typeTable === 'rpeTable') {
                if (value > 10) value = 10
                if (value < 7) value = 7
            }
            if (typeTable === 'rirTable') {
                if (value > 4) value = 4
                if (value < 0) value = 0
            }
            let findValueTable = vm[typeTable]?.find(table => table.name == value) || null
            let reps = set.data.find(data => data.short_name === 'short_reps') || null
            let repsValue = reps && (Number(reps.value || 0) - 1) || 0
            if (repsValue > 7) repsValue = 7
            if (repsValue < 0) repsValue = 0
            return findValueTable && repsValue && findValueTable.reps[repsValue] || 0
        },
    },
    watch: {
        type() {
            this.setCharts()
        },
        docs() {
            this.setCharts()
        },
        compareDocs() {
            this.setCharts()
        },
        currentGroupBy() {
            this.setCharts()
        }
    }
}
</script>

<style scoped>
.vc-metric {
    flex: 1;
    max-width: 220px;
}

.vc-metric ::v-deep .v-input,
.vc-metric ::v-deep .v-text-field {
    margin: 0;
    padding: 0;
}

.vc-metric ::v-deep .v-input__control,
.vc-metric ::v-deep .v-input__slot {
    min-height: 30px !important;
}

.vc-metric ::v-deep .v-input__slot {
    padding: 0 10px !important;
}

.vc-metric ::v-deep .v-select__selection {
    font-size: 12px;
    font-weight: 600;
    text-transform: none;
    letter-spacing: normal;
    color: var(--v-txtSecondary-base);
}

.vc-metric ::v-deep .v-input__append-inner {
    margin-top: 0;
    align-self: center;
}

.vc-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    margin-bottom: 8px;
    flex-shrink: 0;
}

.vc-legend {
    display: flex;
    gap: 12px;
    flex-wrap: wrap;
}

.vc-leg {
    display: flex;
    align-items: center;
    gap: 5px;
    font-size: 10px;
    font-weight: 600;
    color: var(--v-txtMuted-base);
}

.vc-leg-sq {
    width: 10px;
    height: 10px;
    border-radius: 2px;
    flex-shrink: 0;
}

.vc-leg-dot {
    width: 10px;
    height: 10px;
    border-radius: 50%;
    flex-shrink: 0;
    background: linear-gradient(90deg, #E85C5C 0%, #E6AC00 50%, #339999 100%);
}

.vc-wdots {
    margin-top: 6px;
    flex-shrink: 0;
}

.vc-wdots-track {
    position: relative;
    width: 100%;
    height: 12px;
}

.vc-wdot-slot {
    position: absolute;
    top: 50%;
    transform: translate(-50%, -50%);
    display: flex;
    align-items: center;
    justify-content: center;
    line-height: 0;
}

.vc-wdot {
    display: block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    cursor: default;
}

.vc-wdot--empty {
    background: var(--v-surface2-base);
    border: 1px solid var(--v-outline-base);
}
</style>

<style>
.vc-wtt.v-tooltip__content {
    opacity: 1 !important;
    padding: 8px 10px;
    border-radius: 10px;
    border: 1px solid var(--v-outline-base);
    box-shadow: 0 12px 32px rgba(0, 0, 0, 0.28);
}

.vc-wtt-inner {
    font-family: 'Outfit', sans-serif;
    min-width: 110px;
}

.vc-wtt-date {
    font-size: 10px;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: var(--v-txtMuted-base);
    margin-bottom: 4px;
}

.vc-wtt-row {
    display: flex;
    align-items: center;
    gap: 6px;
    font-size: 11px;
    color: var(--v-txtSecondary-base);
}

.vc-wtt-row b {
    margin-left: auto;
    font-weight: 700;
    color: var(--v-txtPrimary-base);
}
</style>